import React from 'react';
import DOMPurify from 'dompurify';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Link,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useMutation, useQuery } from 'react-query';
import { client } from 'src/utils/api-client';
import { set } from 'lodash';
import Autocomplete from 'src/components/Autocomplete';
import { UploadedFileSchema } from 'src/Ghg/CodeOfConductSurvey/types';
import SingleFileUpload from 'src/components/SingleFileUpload';
import { useLcaImage } from 'src/Lca/hooks';
import { useLcaOrganization } from 'src/common/hooks';
import { UUID } from 'crypto';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import GpcCodeAutocomplete from '../../components/GpcCodeAutocomplete';
import MultipleFilesUpload from '../../components/MultipleFilesUpload';

interface LcaIndustryCode {
  code: string;
  description: string;
}

interface LcaProductGtinOutSchema {
  gtin_code: string;
  name: string;
  description: string;
  image_id: UUID | null;
  gpc_code: string | null;
}

export default function AdditionalInfo({
  setValues,
  values,
}: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'basicInfo' });
  const { data: cpcData } = useQuery('getCpc', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/cpc');
  });

  const { data: naceData } = useQuery('getNace', () => {
    return client.get<LcaIndustryCode[]>('/web/industries/nace');
  });

  const organization = useLcaOrganization();

  const naceOptional = values.nace_industries.slice(1);
  const importGtin = useMutation(() =>
    client.post<LcaProductGtinOutSchema>(
      `/web/lca/product-codes/gtin/${values.gtin_code}/import-as-product`,
      { organization_id: organization.id }
    )
  );

  const removeNace = (i: number) => {
    setValues({
      ...values,
      nace_industries: values.nace_industries.splice(i, 1),
    });
  };
  const removeCpc = () => {
    setValues({
      ...values,
      cpc_industry: '',
    });
  };
  const addNace = () => {
    setValues({
      ...values,
      nace_industries: [...values.nace_industries, { code: '' }],
    });
  };
  const handleNace = (value: string | null, i: number) => {
    const temp = { ...values };
    set(temp, `nace_industries[${i}].code`, value);
    setValues(temp);
  };

  const handleCpc = (value: string | null) => {
    setValues({ ...values, cpc_industry: { code: value } });
  };

  const handleDevelopmentFlowchartsDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      development_flowcharts_description: e.target.value,
    });
  };

  const handleTechnicalDocumentationDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      technical_documentation_description: e.target.value,
    });
  };

  const handleRelatedDocumentsDescription = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({
      ...values,
      related_documents_description: e.target.value,
    });
  };

  const handleManufacturerName = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, manufacturer_name: e.target.value });
  };

  const handleKeyCompanyActivities = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, key_company_activities: e.target.value });
  };

  const handleProductCategoryRules = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, product_category_rules: e.target.value });
  };

  const handleRevision = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, revision: e.target.value });
  };

  const handleLcaPreparationDate = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, lca_preparation_date: e.target.value });
  };

  const handleValidUntil = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValues({ ...values, valid_until: e.target.value });
  };

  const handleCeMark = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, ce_mark: e.target.checked });
  };

  const handleImageAdd = (image: UploadedFileSchema) => {
    setValues({ ...values, image_id: image.id });
  };
  const handleImageRemove = () => {
    setValues({ ...values, image_id: null });
  };

  const { image } = useLcaImage(values.image_id ?? '');

  const handleGtinCode = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const rawValue = e.target.value;
    // Keep only digits
    const sanitizedValue = rawValue.replace(/\D/g, '');
    setValues({ ...values, gtin_code: sanitizedValue ?? '' });
  };

  const getGtinErrorMessage = () => {
    if (!values.gtin_code) return null;
    if (![8, 12, 13, 14].includes(values.gtin_code?.length)) {
      return t('errorGtinLength');
    }
    const expectedChecksum = values.gtin_code[values.gtin_code.length - 1];
    const gtinWithoutChecksum = values.gtin_code.slice(0, -1);
    const calculateChecksum = () => {
      const gtinArray = gtinWithoutChecksum.split('').map(Number);
      const gtinArrayWithWeights = gtinArray.map((digit, i) => {
        if (i % 2 === 0) {
          return digit * 3;
        } else {
          return digit;
        }
      });
      const sum = gtinArrayWithWeights.reduce((acc, curr) => acc + curr, 0);
      const checksum = (10 - (sum % 10)) % 10;
      return checksum;
    };
    const checksum = calculateChecksum();
    if (Number(expectedChecksum) !== checksum) {
      return 'Invalid GTIN code';
    }
    return null;
  };

  const invalidGtinMessage = getGtinErrorMessage();
  const isInvalidGtin = !!invalidGtinMessage;

  const importGtinCode = () => {
    if (isInvalidGtin) return;
    importGtin
      .mutateAsync()
      .then(({ data }) => {
        setValues({
          ...values,
          name: data.name,
          description: data.description,
          image_id: data.image_id,
          gpc_code: data.gpc_code,
        });
        toast.success('GTIN code imported');
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          toast.error('GTIN code not found');
        } else {
          toast.error('Failed to import GTIN code');
        }
      });
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '24px',
        flexDirection: 'column',
      }}
    >
      <StyledTextField
        fullWidth
        label={t('developmentFlowchartsDescription')}
        name="development_flowcharts_description"
        value={values.development_flowcharts_description}
        onChange={handleDevelopmentFlowchartsDescription}
        multiline
        rows={3}
      />
      <MultipleFilesUpload
        attachmentType="development-flowchart"
        productId={values.id}
      />
      <FormControl fullWidth sx={{ mt: '16px' }}>
        <Autocomplete
          label={t('mainNaceCode')}
          placeholder={t('mainNaceCode')}
          options={naceData?.data.map((option: LcaIndustryCode) => ({
            value: option.code,
            label: `${option.code} ${option.description}`,
          }))}
          value={values.nace_industries[0].code ?? ''}
          onChange={(value: string | null) => handleNace(value, 0)}
          sx={{
            backgroundColor:
              values.nace_industries[0].code ?? '' ? '#F6F6F6' : 'inherit',
          }}
          handleClearClick={() =>
            setValues({
              ...values,
              nace_industries: [...values.nace_industries, { code: '' }],
            })
          }
          inputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography variant="captionText">
                    {t('codes.NACE')}
                  </Typography>
                }
                {...{ interactive: true }}
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </FormControl>
      {naceOptional.length >= 1 &&
        naceOptional.map((_, i: number) => (
          <div
            key={i}
            style={{ display: 'flex', alignItems: 'center', gap: 24 }}
          >
            <FormControl fullWidth>
              <Autocomplete
                label={t('optionalAdditionalNaceCodes')}
                placeholder={t('optionalAdditionalNaceCodes')}
                options={naceData?.data.map((option: LcaIndustryCode) => ({
                  value: option.code,
                  label: `${option.code} ${option.description}`,
                }))}
                value={naceOptional[i].code}
                onChange={(value: string | null) => handleNace(value, i + 1)}
                sx={{
                  backgroundColor:
                    values.nace_industries[0].code ?? ''
                      ? '#F6F6F6'
                      : 'inherit',
                }}
                handleClearClick={() => removeNace(i)}
              />
            </FormControl>
            <Button
              variant="secondary"
              size="small"
              onClick={() => removeNace(i)}
            >
              {t('remove')}
            </Button>
          </div>
        ))}
      <div>
        <Button
          variant="secondary"
          size="small"
          onClick={addNace}
          disabled={
            values.nace_industries.length > 9 ||
            values.nace_industries[values.nace_industries.length - 1]?.code ===
              ''
          }
        >
          {t('addNaceCode')}
        </Button>
      </div>
      <FormControl fullWidth>
        <Autocomplete
          label={t('unCpcCode')}
          placeholder={t('unCpcCode')}
          options={cpcData?.data.map((option: LcaIndustryCode) => ({
            value: option.code,
            label: `${option.code} ${option.description}`,
          }))}
          value={values.cpc_industry?.code ?? ''}
          onChange={(value: string | null) => handleCpc(value)}
          sx={{
            backgroundColor:
              values.cpc_industry.code ?? '' ? '#F6F6F6' : 'inherit',
          }}
          handleClearClick={removeCpc}
          inputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <Typography variant="captionText">
                    {t('codes.CPC')}
                  </Typography>
                }
                {...{ interactive: true }}
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      </FormControl>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <StyledTextField
          InputProps={{
            endAdornment: (
              <Tooltip
                title={
                  <>
                    <Typography variant="captionText">
                      {t('codes.GTIN')}
                    </Typography>
                    <Link
                      href="https://gs1pl.org/"
                      target="_blank"
                      rel="noopener"
                      underline="none"
                    >
                      <Typography color="white" variant="captionText">
                        {' '}
                        {t('codes.learnMore')}
                      </Typography>
                    </Link>
                  </>
                }
                {...{ interactive: true }}
                arrow
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
          fullWidth
          label="GTIN code (optional)"
          name="gtin"
          value={values.gtin_code}
          onChange={handleGtinCode}
          error={!!invalidGtinMessage}
          helperText={invalidGtinMessage}
        />

        <Button
          variant="secondary"
          size="small"
          onClick={importGtinCode}
          disabled={!values.gtin_code || isInvalidGtin}
        >
          {t('importData')}
        </Button>
      </Box>
      <FormControl fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GpcCodeAutocomplete
            inputProps={{
              endAdornment: (
                <Tooltip
                  title={
                    <>
                      <Typography variant="captionText">
                        {t('codes.GPC')}
                      </Typography>
                      <Link
                        href="https://gs1pl.org/"
                        target="_blank"
                        rel="noopener"
                        underline="none"
                      >
                        <Typography color="white" variant="captionText">
                          {' '}
                          {t('codes.learnMore')}
                        </Typography>
                      </Link>
                    </>
                  }
                  {...{ interactive: true }}
                  arrow
                >
                  <IconButton>
                    <HelpIcon />
                  </IconButton>
                </Tooltip>
              ),
            }}
            value={values.gpc_code}
            onChange={(code) =>
              setValues({ ...values, gpc_code: code?.code ?? null })
            }
          />
        </Box>
      </FormControl>
      {image && (
        <Box
          component="img"
          src={image.url}
          sx={{
            objectFit: 'contain',
            maxWidth: '400px',
            maxHeight: '400px',
          }}
        />
      )}
      <SingleFileUpload
        url={`/web/organizations/${organization.id}/images`}
        attachButtonText={t('uploadProductImage')}
        onSuccess={handleImageAdd}
        onRemoved={handleImageRemove}
        file={image}
      />
      <StyledTextField
        fullWidth
        label={t('technicalDocumentationDescription')}
        name="technical_documentation_description"
        value={values.technical_documentation_description}
        onChange={handleTechnicalDocumentationDescription}
        multiline
        rows={3}
        InputProps={{
          endAdornment: (
            <Tooltip
              title={
                <Typography
                  variant="captionText"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      t('technicalDocumentationTooltip')
                    ),
                  }}
                />
              }
              {...{ interactive: true }}
              arrow
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
      <MultipleFilesUpload
        attachmentType="technical-documentation"
        productId={values.id}
      />
      <StyledTextField
        fullWidth
        label={t('relatedDocumentsDescription')}
        name="related_documents_description"
        value={values.related_documents_description}
        onChange={handleRelatedDocumentsDescription}
        multiline
        rows={3}
      />
      <MultipleFilesUpload
        attachmentType="related-documents"
        productId={values.id}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography>{t('CeMark')}</Typography>
        <Switch checked={values.ce_mark} onChange={handleCeMark} />
        <Tooltip
          title={
            <Typography variant="captionText">
              {t('ceMarkingTooltip')}
            </Typography>
          }
          {...{ interactive: true }}
          arrow
        >
          <IconButton>
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <StyledTextField
        fullWidth
        label={t('manufacturerName')}
        name="manufacturer_name"
        value={values.manufacturer_name}
        onChange={handleManufacturerName}
      />
      <StyledTextField
        fullWidth
        label={t('keyCompanyActivities')}
        name="key_company_activities"
        value={values.key_company_activities}
        onChange={handleKeyCompanyActivities}
      />
      <StyledTextField
        fullWidth
        label={t('productCategoryRules')}
        name="product_category_rules"
        value={values.product_category_rules}
        onChange={handleProductCategoryRules}
        InputProps={{
          endAdornment: (
            <Tooltip
              title={
                <Typography variant="captionText">
                  {t('productCategoryRulesTooltip')}
                </Typography>
              }
              {...{ interactive: true }}
              arrow
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
      <StyledTextField
        fullWidth
        label={t('revision')}
        name="revision"
        value={values.revision}
        onChange={handleRevision}
        InputProps={{
          endAdornment: (
            <Tooltip
              title={
                <Typography variant="captionText">
                  {t('revisionTooltip')}
                </Typography>
              }
              {...{ interactive: true }}
              arrow
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          ),
        }}
      />
      <StyledTextField
        fullWidth
        label={t('lcaPreparationDate')}
        name="lca_preparation_date"
        value={values.lca_preparation_date}
        onChange={handleLcaPreparationDate}
      />
      <StyledTextField
        fullWidth
        label={t('validUntil')}
        name="valid_until"
        value={values.valid_until}
        onChange={handleValidUntil}
      />
    </div>
  );
}
